import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createFuncionario, buscarPorCpf } from '../../api/funcionarios';
import { getEmpresas } from '../../api/empresas';
import { formatCpf, isValidCpf } from '../../utils/cpf';
import { useAuth } from '../../contexts/AuthContext';

export default function FuncionarioNovo() {
  const navigate = useNavigate();
  const { isFenix } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(isFenix);
  const [form, setForm] = useState({ nome: '', cpf: '', data_nascimento: '', sexo: '', id_empresa: '' });
  const [existente, setExistente] = useState(null); // dados do funcionário já cadastrado
  const [buscandoCpf, setBuscandoCpf] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isFenix) {
      getEmpresas().then(setEmpresas).finally(() => setLoadingEmpresas(false));
    }
  }, [isFenix]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleCpfChange = async (e) => {
    const formatted = formatCpf(e.target.value);
    setForm(prev => ({ ...prev, cpf: formatted }));
    setExistente(null);
    setError('');

    const digits = formatted.replace(/\D/g, '');
    if (digits.length === 11) {
      if (!isValidCpf(formatted)) {
        setError('CPF inválido — verifique se os dígitos estão corretos');
        return;
      }
      setBuscandoCpf(true);
      const found = await buscarPorCpf(digits);
      setBuscandoCpf(false);
      if (found) {
        setExistente(found);
        setForm(prev => ({
          ...prev,
          nome: found.nome || '',
          data_nascimento: found.data_nascimento || '',
          sexo: found.sexo || '',
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidCpf(form.cpf)) {
      setError('CPF inválido — verifique se os dígitos estão corretos');
      return;
    }
    if (isFenix && !form.id_empresa) {
      setError('Selecione a empresa do funcionário');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome,
        cpf: form.cpf.replace(/\D/g, ''),
        data_nascimento: form.data_nascimento || null,
        sexo: form.sexo || null,
      };
      if (isFenix) payload.id_empresa = form.id_empresa;
      const novo = await createFuncionario(payload);
      navigate(`/funcionarios/${novo.id_funcionario}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cadastrar funcionário');
    } finally {
      setSaving(false);
    }
  };

  if (loadingEmpresas) return <LoadingSpinner />;

  const bloqueado = !!existente;

  return (
    <>
      <button className="btn btn-sm btn-link text-muted mb-3 ps-0" onClick={() => navigate('/funcionarios')}>
        <i className="bi bi-arrow-left me-1"></i>Voltar para funcionários
      </button>

      <PageHeader title="Novo funcionário" subtitle="Preencha os dados para cadastrar" />

      <div className="card-datafit p-4" style={{ maxWidth: 560 }}>
        {error && (
          <div className="alert alert-danger py-2 small mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>{error}
          </div>
        )}

        {bloqueado && (
          <div className="alert alert-info py-2 small mb-3">
            <i className="bi bi-person-check me-2"></i>
            Funcionário já cadastrado no sistema. Será adicionado apenas o vínculo com a empresa selecionada.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Empresa — fenix/admin apenas */}
          {isFenix && (
            <div className="mb-3">
              <label className="form-label small fw-500">Empresa *</label>
              <select className="form-select" value={form.id_empresa} onChange={set('id_empresa')} required>
                <option value="">Selecione a empresa...</option>
                {empresas.map(e => (
                  <option key={e.id_empresa} value={e.id_empresa}>
                    {e.nome_fantasia || e.razao_social}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* CPF — sempre editável (é o campo de lookup) */}
          <div className="mb-3">
            <label className="form-label small fw-500">CPF *</label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control"
                placeholder="000.000.000-00"
                value={form.cpf}
                onChange={handleCpfChange}
                required
              />
              {buscandoCpf && (
                <span className="position-absolute end-0 top-50 translate-middle-y me-3">
                  <span className="spinner-border spinner-border-sm text-muted" />
                </span>
              )}
            </div>
          </div>

          {/* Nome */}
          <div className="mb-3">
            <label className="form-label small fw-500">Nome completo *</label>
            <input
              type="text"
              className="form-control"
              value={form.nome}
              onChange={set('nome')}
              disabled={bloqueado}
              style={bloqueado ? { background: '#e9ecef', color: '#495057' } : {}}
              required
            />
          </div>

          <div className="row g-3 mb-4">
            <div className="col-sm-6">
              <label className="form-label small fw-500">Data de nascimento</label>
              <input
                type="date"
                className="form-control"
                value={form.data_nascimento}
                onChange={set('data_nascimento')}
                disabled={bloqueado}
                style={bloqueado ? { background: '#e9ecef', color: '#495057' } : {}}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label small fw-500">Sexo</label>
              <select
                className="form-select"
                value={form.sexo}
                onChange={set('sexo')}
                disabled={bloqueado}
                style={bloqueado ? { background: '#e9ecef', color: '#495057' } : {}}
              >
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
              </select>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn-datafit" disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-check-lg me-2"></i>}
              {bloqueado ? 'Adicionar vínculo' : 'Salvar'}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/funcionarios')}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
